import type { Fare } from '@/types/pricing';

/**
 * Format Amadeus response text for display
 * Converts carriage returns to newlines
 */
export function formatAmadeusResponse(text: string): string {
  if (!text) return text;
  return text.replace(/\r/g, '\n');
}

/**
 * Check if the response indicates no fares were found
 */
export function checkNoFaresFound(text: string): boolean {
  if (!text) return false;
  return text.includes('NO FARES/RBD/CARRIER/PASSENGER');
}

/**
 * Extract fare information from raw Amadeus response text
 * This is a fallback for when backend doesn't return structured data
 *
 * Looks for patterns like:
 * *T01 JCOUPON+F ... *1234.00
 * *Y02 FFF77WW ... *567.89
 */
export function extractFareInfo(text: string): Fare[] {
  if (!text) return [];

  const fares: Fare[] = [];
  const lines = text.split('\n');

  for (const line of lines) {
    // Look for lines containing *T or *Y (fare type indicators)
    if (line.includes('*T') || line.includes('*Y')) {
      const parts = line.split('*');

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();

        // Match patterns like "T01 JCOUPON+F" or "Y02 FFF77WW"
        const match = part.match(/^([TY])\r?(\d+)\s+(.+)$/);
        if (match) {
          const fareNumber = match[2];
          const fareBase = match[3].trim();

          // Look for price in subsequent parts
          for (let j = i + 1; j < Math.min(i + 5, parts.length); j++) {
            const pricePart = parts[j].trim();
            const priceMatch = pricePart.match(/(\d+\.\d+)/);

            if (priceMatch) {
              const price = parseFloat(priceMatch[1]);

              if (!isNaN(price)) {
                fares.push({
                  id: parseInt(fareNumber, 10).toString(),
                  farebase: fareBase,
                  price: price,
                });
                break;
              }
            }
          }
        }
      }
    }
  }

  return fares;
}

/**
 * Format a price for display with Euro symbol
 */
export function formatPrice(price: number): string {
  return `€${price.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
